import React, { useState, useEffect } from "react";
import { Grid, Box, Typography, Button } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Modified Carousel Component with props
const Carousel = ({ images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Default images if no images prop is provided
  const defaultImages = [
    {
      id: 1,
      src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQY3XY_XmjDNP-g3oSYSflusOyiGgGKwD3p5w&s",
      alt: "Default image 1",
    },
    {
      id: 2,
      src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQY3XY_XmjDNP-g3oSYSflusOyiGgGKwD3p5w&s",
      alt: "Default image 2",
    },
    {
      id: 3,
      src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQY3XY_XmjDNP-g3oSYSflusOyiGgGKwD3p5w&s",
      alt: "Default image 3",
    },
  ];

  // Use provided images or fall back to default
  const carouselImages = images.length > 0 ? images : defaultImages;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + carouselImages.length) % carouselImages.length
    );
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying, carouselImages.length]);

  const handleUserInteraction = (callback) => {
    return () => {
      setIsAutoPlaying(false);
      callback();
      setTimeout(() => setIsAutoPlaying(true), 8000);
    };
  };

  const getVisibleImages = () => {
    const visibleImages = [];
    const displayCount = Math.min(3, carouselImages.length);
    for (let i = 0; i < displayCount; i++) {
      const index = (currentIndex + i) % carouselImages.length;
      visibleImages.push(carouselImages[index]);
    }
    return visibleImages;
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* Images Container with Overflow */}
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          mb: 3,
          display: "flex",
          justifyContent: "flex-start",
          pl: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          {getVisibleImages().map((image, index) => (
            <Box
              key={`${image.id}-${currentIndex}-${index}`}
              sx={{
                width: { xs: "140px", sm: "160px", md: "180px", lg: "230px" },
                height: { xs: "140px", sm: "160px", md: "180px", lg: "300px" },
                backgroundColor: "white",
                borderRadius: "5px",
                padding: "6px",
                flexShrink: 0,
                transform: "scale(1)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                // Cut off the third image partially
                ...(index === 2 && {
                  marginRight: "-60px",
                  overflow: "hidden",
                }),
              }}
              onClick={() =>
                handleUserInteraction(() => {
                  const targetIndex =
                    (currentIndex + index) % carouselImages.length;
                  setCurrentIndex(targetIndex);
                })()
              }
            >
              <img
                src={image.src}
                alt={image.alt}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/200x200/cccccc/666666?text=Image";
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Navigation Controls and Progress Bar Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          mb: 3,
          width: "100%",
          justifyContent: "center",
        }}
      >
        {/* Navigation Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            onClick={handleUserInteraction(prevSlide)}
            sx={{
              minWidth: { xs: "45px", sm: "50px" },
              width: { xs: "45px", sm: "50px" },
              height: { xs: "45px", sm: "50px" },
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: "50%",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              color: "white",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
                transform: "scale(1.05)",
              },
            }}
          >
            <ChevronLeft size={24} strokeWidth={3} />
          </Button>

          <Button
            onClick={handleUserInteraction(nextSlide)}
            sx={{
              minWidth: { xs: "45px", sm: "50px" },
              width: { xs: "45px", sm: "50px" },
              height: { xs: "45px", sm: "50px" },
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: "50%",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              color: "white",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
                transform: "scale(1.05)",
              },
            }}
          >
            <ChevronRight size={24} strokeWidth={3} />
          </Button>
        </Box>

        {/* Progress Bar */}
        <Box
          sx={{
            width: { xs: "250px", sm: "300px", md: "350px" },
            height: "6px",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            borderRadius: "3px",
            overflow: "hidden",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box
            sx={{
              height: "100%",
              backgroundColor: "white",
              width: `${((currentIndex + 1) / carouselImages.length) * 100}%`,
              transition: "width 0.5s ease",
              borderRadius: "3px",
              boxShadow: "0 0 10px rgba(255, 255, 255, 0.5)",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Carousel;
